'use client'

import { useCallback, useState } from 'react'
import Button from '@components/Furry/Button'
import { useTranslation } from 'react-i18next'
import SimpleSpinner from '@components/SimpleSpinner'
import { Holding } from '@hooks/useHoldings'
import { StakedListing } from './HoldingDetails.type'
import {
  auctionManagerKey,
  claimedMetadataKey,
  init as initAuctionManager,
} from '@dewicats/auction-manager-sdk'
import { useAsync } from 'react-async-hook'
import { DEWICATS_COLLECTION } from '@utils/constants'
import { useSolana } from '@dewicats/connect-button'
import { TransactionInstruction } from '@solana/web3.js'
import { bulkSendRawTransactions } from '@helium/spl-utils'
import { batchInstructionsToTxsWithPriorityFee } from '@helium/spl-utils'
import { toast } from '@hooks/use-toast'
import { Transaction } from '@solana/web3.js'

const ESTIMAED_SOL_REFUND_PER_ACCOUNT = 0.001405789474

const ClaimSolButton = ({ holding }: { holding: Holding }) => {
  const { t } = useTranslation()
  const { anchorProvider } = useSolana()
  const [claiming, setClaiming] = useState(false)
  const [claimed, setClaimed] = useState(false)

  const { result: claimMetadata, loading: claimMetadataLoading } =
    useAsync(async () => {
      if (!anchorProvider) {
        return null
      }

      const auctionManagerProgram = await initAuctionManager(anchorProvider)
      const [auctionKey] = auctionManagerKey(DEWICATS_COLLECTION, 'dewicats')
      const [claimMetadataK] = claimedMetadataKey(holding.publicKey, auctionKey)

      try {
        const claimMetadata =
          await auctionManagerProgram.account.claimedMetadataV0.fetch(
            claimMetadataK,
          )
        return claimMetadata
      } catch (error) {
        return null
      }
    }, [anchorProvider])

  const {
    result: stakeReciepts,
    loading: stakeRecieptsLoading,
    execute: executeStakeReciepts,
  } = useAsync(async () => {
    if (!anchorProvider) {
      return null
    }

    const auctionManagerProgram = await initAuctionManager(anchorProvider)

    const stakeReciepts =
      await auctionManagerProgram.account.stakeRecieptV0.all([
        {
          memcmp: {
            offset: 8,
            bytes: holding.publicKey.toBase58(),
          },
        },
      ])

    return stakeReciepts
  }, [anchorProvider, holding.publicKey])

  const handleClaimSol = useCallback(async (): Promise<void | null> => {
    setClaiming(true)

    if (!anchorProvider) {
      return null
    }

    const auctionManagerProgram = await initAuctionManager(anchorProvider)
    const [auctionKey] = auctionManagerKey(DEWICATS_COLLECTION, 'dewicats')

    const instructions: TransactionInstruction[] = []

    for (const stakeReciept of stakeReciepts) {
      const closeStakeRecieptInstruction = await auctionManagerProgram.methods
        .closeStakeRecieptV0({})
        .accountsPartial({
          claimedMetadata: claimedMetadataKey(holding.publicKey, auctionKey)[0],
          payer: anchorProvider.wallet.publicKey,
          stakeReciept: stakeReciept.publicKey,
        })
        .instruction()

      instructions.push(closeStakeRecieptInstruction)
    }

    const transactions = await batchInstructionsToTxsWithPriorityFee(
      anchorProvider,
      instructions,
      { basePriorityFee: 0 },
    )

    for (const transactionDraft of transactions) {
      toast({
        title: 'Claiming SOL...',
        description: 'Transaction is being processed on Solana',
        variant: 'default',
      })

      const transaction = new Transaction()
      transaction.add(...transactionDraft.instructions)
      transaction.recentBlockhash = transactionDraft.recentBlockhash
      transaction.feePayer = transactionDraft.feePayer

      try {
        const signedTxn =
          await anchorProvider.wallet.signTransaction(transaction)
        const sigs = await bulkSendRawTransactions(anchorProvider.connection, [
          signedTxn.serialize(),
        ])

        if (!sigs || sigs.length === 0) {
          throw new Error('Transaction Error')
        }

        toast({
          title: 'Success!',
          description: 'Successfully claimed SOL',
          variant: 'default',
        })
      } catch (err) {
        toast({
          title: 'Failed to claim SOL',
          description: 'Please try again',
          variant: 'destructive',
          action: (
            <button
              onClick={handleClaimSol}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 px-3 py-2 rounded-md text-xs font-medium"
            >
              Try Again
            </button>
          ),
        })
        throw err
      }
    }

    setClaiming(false)
    setClaimed(true)
    executeStakeReciepts()
  }, [anchorProvider, stakeReciepts, holding, executeStakeReciepts])

  if (!claimMetadata || !stakeReciepts || stakeReciepts.length === 0) {
    return null
  }

  return (
    <Button
      ripple
      className={`text-xs px-8 ${
        claiming || claimed || stakeRecieptsLoading || claimMetadataLoading
          ? 'pointer-events-none'
          : ''
      }`}
      disabled={
        claiming || claimed || stakeRecieptsLoading || claimMetadataLoading
      }
      onClick={handleClaimSol}
    >
      {claiming || stakeRecieptsLoading ? (
        <SimpleSpinner size={'small'} />
      ) : (
        t('holdings.details.claimSol', {
          amount: stakeReciepts.length * ESTIMAED_SOL_REFUND_PER_ACCOUNT,
        })
      )}
    </Button>
  )
}

export default ClaimSolButton
